import { grpcClientFactory } from './grpcClientFactory';
import { protoAuthorToTs } from './wireShapes';
import { GrpcCallError } from '../utils/grpcErrors';
function protoAttachmentToTs(protoAttachment) {
    return {
        id: protoAttachment.getId() || '',
        filename: protoAttachment.getFilename() || '',
        contentType: protoAttachment.getContentType() || '',
        sizeBytes: protoAttachment.getSizeBytes() || 0,
        presignedUrl: protoAttachment.getPresignedUrl() || ''
    };
}
function protoAnchorToTs(protoAnchor) {
    if (!protoAnchor)
        return undefined;
    const anchor = {
        latitude: protoAnchor.getLatitude() || 0,
        longitude: protoAnchor.getLongitude() || 0
    };
    // proto3 optional: hasHeight() exists when the field was set
    if (typeof protoAnchor.hasHeight === 'function' && protoAnchor.hasHeight()) {
        anchor.height = protoAnchor.getHeight();
    }
    return anchor;
}
function protoCommentToTs(protoComment) {
    const replies = (protoComment.getRepliesList() || []).map(protoCommentToTs);
    const attachments = (protoComment.getAttachmentsList() || []).map(protoAttachmentToTs);
    // [DEBUG round5] Tag visibility diagnostic — remove once verified working.
    // eslint-disable-next-line no-console
    console.log('[protoCommentToTs]', protoComment.getId(), 'tags=', protoComment.getTagsList());
    return {
        id: protoComment.getId() || '',
        projectId: protoComment.getProjectId() || '',
        scopeId: protoComment.getScopeId() || undefined,
        parentCommentId: protoComment.getParentCommentId() || undefined,
        content: protoComment.getContent() || '',
        deleted: protoComment.getDeleted() || false,
        author: protoAuthorToTs(protoComment.getAuthor()),
        replies,
        replyCount: protoComment.getReplyCount() || 0,
        attachments,
        artifactIds: protoComment.getArtifactIdsList() || [],
        anchor: protoAnchorToTs(protoComment.getAnchor()),
        mentionedUserIds: protoComment.getMentionedUserIdsList() || [],
        resolved: protoComment.getResolved() || false,
        tags: protoComment.getTagsList() || [],
        createdAt: protoComment.getCreatedAt() || 0,
        updatedAt: protoComment.getUpdatedAt() || 0
    };
}
function tsAnchorToProto(anchor) {
    const Anchor = proto.landscope.mapviewer.v1.CommentAnchor;
    const grpcAnchor = new Anchor();
    grpcAnchor.setLatitude(anchor.latitude);
    grpcAnchor.setLongitude(anchor.longitude);
    if (anchor.height !== undefined)
        grpcAnchor.setHeight(anchor.height);
    return grpcAnchor;
}
class CommentService {
    constructor() {
        this.client = grpcClientFactory.createUserProjectsClient({
            withConditionalAuth: true,
            enableCompression: true
        });
    }
    clearClient() {
        this.client = grpcClientFactory.createUserProjectsClient({
            withConditionalAuth: true,
            enableCompression: true
        });
    }
    async createComment(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.CreateCommentRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(request.projectId);
                grpcReq.setContent(request.content);
                if (request.scopeId)
                    grpcReq.setScopeId(request.scopeId);
                if (request.parentCommentId)
                    grpcReq.setParentCommentId(request.parentCommentId);
                if (request.artifactIds && request.artifactIds.length > 0) {
                    grpcReq.setArtifactIdsList(request.artifactIds);
                }
                if (request.anchor) {
                    grpcReq.setAnchor(tsAnchorToProto(request.anchor));
                }
                if (request.mentionedUserIds && request.mentionedUserIds.length > 0) {
                    grpcReq.setMentionedUserIdsList(request.mentionedUserIds);
                }
                if (request.tags && request.tags.length > 0) {
                    grpcReq.setTagsList(request.tags);
                }
                // [DEBUG round5] Tag visibility diagnostic — remove once verified working.
                // eslint-disable-next-line no-console
                console.log('[createComment outbound] tags=', request.tags);
                this.client.createComment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to create comment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: resp.getSuccess(),
                        message: resp.getMessage(),
                        commentId: resp.getCommentId()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to create comment: ${e}`));
            }
        });
    }
    async replyToComment(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ReplyToCommentRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(request.projectId);
                grpcReq.setParentCommentId(request.parentCommentId);
                grpcReq.setContent(request.content);
                if (request.scopeId)
                    grpcReq.setScopeId(request.scopeId);
                if (request.additionalArtifactIds && request.additionalArtifactIds.length > 0) {
                    grpcReq.setAdditionalArtifactIdsList(request.additionalArtifactIds);
                }
                if (request.mentionedUserIds && request.mentionedUserIds.length > 0) {
                    grpcReq.setMentionedUserIdsList(request.mentionedUserIds);
                }
                this.client.replyToComment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to reply to comment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: resp.getSuccess(),
                        message: resp.getMessage(),
                        commentId: resp.getCommentId()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to reply to comment: ${e}`));
            }
        });
    }
    async updateComment(request) {
        return new Promise((resolve, reject) => {
            var _a;
            try {
                const Req = proto.landscope.mapviewer.v1.UpdateCommentRequest;
                const grpcReq = new Req();
                grpcReq.setCommentId(request.commentId);
                grpcReq.setContent(request.content);
                // Tags on the wire are authoritative — callers should send the
                // existing tags they want preserved alongside any additions.
                grpcReq.setTagsList((_a = request.tags) !== null && _a !== void 0 ? _a : []);
                this.client.updateComment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to update comment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: resp.getSuccess(),
                        message: resp.getMessage()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to update comment: ${e}`));
            }
        });
    }
    async deleteComment(commentId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.DeleteCommentRequest;
                const grpcReq = new Req();
                grpcReq.setCommentId(commentId);
                this.client.deleteComment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to delete comment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: resp.getSuccess(),
                        message: resp.getMessage()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to delete comment: ${e}`));
            }
        });
    }
    async getComments(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetCommentsRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(request.projectId);
                if (request.scopeId)
                    grpcReq.setScopeId(request.scopeId);
                if (request.limit)
                    grpcReq.setLimit(request.limit);
                if (request.cursor) {
                    const Cursor = proto.landscope.mapviewer.v1.CommentCursor;
                    const cursor = new Cursor();
                    cursor.setCreatedAt(request.cursor.createdAt);
                    cursor.setCommentId(request.cursor.commentId);
                    grpcReq.setCursor(cursor);
                }
                this.client.getComments(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new GrpcCallError(`Failed to get comments: ${err.message}`, err.code));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const comments = (resp.getCommentsList() || []).map(protoCommentToTs);
                    const nextCursorProto = resp.getNextCursor();
                    const nextCursor = nextCursorProto ? {
                        createdAt: nextCursorProto.getCreatedAt(),
                        commentId: nextCursorProto.getCommentId()
                    } : undefined;
                    resolve({
                        comments,
                        nextCursor,
                        hasMore: resp.getHasMore()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to get comments: ${e}`));
            }
        });
    }
    async getComment(commentId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetCommentRequest;
                const grpcReq = new Req();
                grpcReq.setCommentId(commentId);
                this.client.getComment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to get comment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve(protoCommentToTs(resp.getComment()));
                });
            }
            catch (e) {
                reject(new Error(`Failed to get comment: ${e}`));
            }
        });
    }
    async listProjectTags(projectId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ListProjectCommentTagsRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                this.client.listProjectCommentTags(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new GrpcCallError(`Failed to list project comment tags: ${err.message}`, err.code));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve(resp.getTagsList() || []);
                });
            }
            catch (e) {
                reject(new Error(`Failed to list project comment tags: ${e}`));
            }
        });
    }
    async getAttachmentUploadURL(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetAttachmentUploadURLRequest;
                const grpcReq = new Req();
                grpcReq.setCommentId(request.commentId);
                grpcReq.setFilename(request.filename);
                grpcReq.setContentType(request.contentType);
                grpcReq.setSizeBytes(request.sizeBytes);
                this.client.getAttachmentUploadURL(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to get attachment upload URL: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        attachmentId: resp.getAttachmentId(),
                        presignedUrl: resp.getPresignedUrl()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to get attachment upload URL: ${e}`));
            }
        });
    }
    async confirmAttachment(attachmentId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ConfirmAttachmentRequest;
                const grpcReq = new Req();
                grpcReq.setAttachmentId(attachmentId);
                this.client.confirmAttachment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to confirm attachment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: resp.getSuccess(),
                        message: resp.getMessage()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to confirm attachment: ${e}`));
            }
        });
    }
    async deleteAttachment(attachmentId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.DeleteAttachmentRequest;
                const grpcReq = new Req();
                grpcReq.setAttachmentId(attachmentId);
                this.client.deleteAttachment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to delete attachment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: resp.getSuccess(),
                        message: resp.getMessage()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to delete attachment: ${e}`));
            }
        });
    }
    async uploadAttachment(commentId, file, onProgress) {
        const uploadUrlResponse = await this.getAttachmentUploadURL({
            commentId,
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size
        });
        // Use XMLHttpRequest for progress tracking (same pattern as DEM upload)
        await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && onProgress) {
                    const progress = Math.round((e.loaded / e.total) * 100);
                    onProgress(progress);
                }
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                }
                else {
                    reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
                }
            };
            xhr.onerror = () => {
                reject(new Error('Network error during upload'));
            };
            xhr.open('PUT', uploadUrlResponse.presignedUrl);
            xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
            xhr.send(file);
        });
        await this.confirmAttachment(uploadUrlResponse.attachmentId);
        return {
            id: uploadUrlResponse.attachmentId,
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
            presignedUrl: ''
        };
    }
}
export const commentService = new CommentService();
